import { ulid } from 'ulid';
import type { ApiClient } from '../api/client';
import { ApiFailure, isRetryable } from '../api/errors';
import { META, readMeta, writeMeta } from '../db/schema';
import type { AppDb } from '../db/schema';
import { ConflictingAppointment } from '../wire';
import { describeIntent } from './intents';
import { isPending } from './entry';
import type { IntentDescription, IntentKind, OutboxEntry, RejectionRecord } from './entry';
import type { Appointment } from '../wire';

/**
 * The outbox — spec §8.2, executed.
 *
 * > Writes are appended to a local outbox: an ordered list of intents, each with
 * > a client-generated ULID and an idempotency key. Online, the outbox drains
 * > immediately and the round trip is invisible. Offline, it queues. On reconnect
 * > it replays in order against `core-api`, where each intent is idempotent — so
 * > a partial replay is safe.
 *
 * Four properties, each of which a view could accidentally break if it were
 * allowed to write to the network directly, which is why none of them may:
 *
 * **1. One code path, online and off.** A tap enqueues and then the drain runs.
 * When there is a network the drain finishes in the time the tap's animation
 * takes and the round trip is invisible; when there is not, the same entry sits
 * in the same store. There is no "if online, POST; else queue" branch anywhere in
 * this app, which is the only way the offline path can be as well tested as the
 * online one — it *is* the online one.
 *
 * **2. Order is total and it is enforced by stopping.** `seq` comes from a
 * counter in `meta`, and the drain walks entries in `seq` order. When one is not
 * ready — backoff, or a transient failure — the drain **stops** rather than
 * skipping ahead. Head-of-line blocking is the feature: "book Dana at 2, then
 * move Dana to 3" replayed out of order is a booking at 2 that nobody moved. A
 * *rejected* entry is different: the server has ruled, the intent leaves the
 * queue, and the drain continues — a task in the owner's list must not stop the
 * other four appointments from landing.
 *
 * **3. The idempotency key never changes.** It is minted at enqueue as
 * `intent:${id}` and stored. Attempt seven sends what attempt one sent, so if
 * attempt one committed and the answer was lost, attempt seven gets that answer
 * back rather than writing a second booking.
 *
 * **4. Conflicts become tasks, never merges.** A 4xx that is not a transient
 * condition moves the entry to `rejected` with the server's own detail attached.
 * Nothing here re-books, re-times, or picks a nearby slot. §8.3: the server wins,
 * and the owner decides.
 */

export interface OutboxSettlement {
  entry: OutboxEntry;
  body: unknown;
}

export interface OutboxOptions {
  db: AppDb;
  api: ApiClient;
  /** Applied to the mirror when an intent settles successfully. */
  onSettled: (settlement: OutboxSettlement) => Promise<void>;
  /** Fired on every state change, so the UI can repaint. */
  onChange: () => void;
  now?: () => number;
}

export interface EnqueueInput {
  kind: IntentKind;
  payload: Record<string, unknown>;
  target?: Record<string, string>;
  describe: IntentDescription;
  optimistic?: Appointment | undefined;
}

/** Exponential, capped. No jitter: one device, and a test that can predict it. */
export function backoffMs(attempts: number): number {
  return Math.min(30_000, 1000 * 2 ** Math.max(0, attempts - 1));
}

export class Outbox {
  private readonly db: AppDb;
  private readonly api: ApiClient;
  private readonly onSettled: (settlement: OutboxSettlement) => Promise<void>;
  private readonly onChange: () => void;
  private readonly now: () => number;
  /** Every drain queues behind the previous one. See `drain()`. */
  private chain: Promise<void> = Promise.resolve();

  constructor(options: OutboxOptions) {
    this.db = options.db;
    this.api = options.api;
    this.onSettled = options.onSettled;
    this.onChange = options.onChange;
    this.now = options.now ?? Date.now;
  }

  /**
   * Append an intent. Returns the entry, already visible to the views.
   *
   * The ULID is the id, which makes `seq` almost redundant — ULIDs sort
   * lexicographically by time. Almost: two intents minted inside the same
   * millisecond sort by their random tail, and "almost always in order" is not
   * an order. The counter is boring and total.
   */
  async enqueue(input: EnqueueInput): Promise<OutboxEntry> {
    const id = ulid();
    const seq = await this.nextSeq();
    const entry: OutboxEntry = {
      id,
      seq,
      kind: input.kind,
      target: input.target ?? {},
      payload: input.payload,
      idempotencyKey: `intent:${id}`,
      createdAt: new Date(this.now()).toISOString(),
      status: 'queued',
      attempts: 0,
      nextAttemptAt: 0,
      describe: input.describe,
      optimistic: input.optimistic,
    };
    await this.db.put('outbox', entry);
    this.onChange();
    void this.drain();
    return entry;
  }

  async all(): Promise<OutboxEntry[]> {
    const rows = await this.db.getAllFromIndex('outbox', 'by-seq');
    return rows;
  }

  async pending(): Promise<OutboxEntry[]> {
    return (await this.all()).filter(isPending);
  }

  async rejected(): Promise<OutboxEntry[]> {
    return (await this.all()).filter((e) => e.status === 'rejected');
  }

  async depth(): Promise<number> {
    return (await this.pending()).length;
  }

  /** Dismiss a task. The intent is gone; nothing is retried, nothing is merged. */
  async dismiss(id: string): Promise<void> {
    await this.db.delete('outbox', id);
    this.onChange();
  }

  /**
   * Put a rejected intent back at the **end** of the queue.
   *
   * At the end, and with a fresh id, because it is a new decision. The owner
   * looked at the task, decided to try again, and that is a new intent — reusing
   * the old ULID would hit the server's memo of the failure and re-inserting it
   * at its old `seq` would replay it before writes that have already landed.
   */
  async retry(id: string): Promise<OutboxEntry | null> {
    const old = await this.db.get('outbox', id);
    if (old === undefined || old.status !== 'rejected') return null;
    await this.db.delete('outbox', id);
    return this.enqueue({
      kind: old.kind,
      payload: old.payload,
      target: old.target,
      describe: old.describe,
      optimistic: old.optimistic,
    });
  }

  /** Forget settled intents. Called after a successful hydrate. */
  async prune(olderThanMs = 60_000): Promise<void> {
    const cutoff = this.now() - olderThanMs;
    for (const entry of await this.all()) {
      if (entry.status !== 'done') continue;
      const at = entry.settledAt === undefined ? 0 : Date.parse(entry.settledAt);
      if (at < cutoff) await this.db.delete('outbox', entry.id);
    }
    this.onChange();
  }

  /**
   * Send everything that is ready, in order, until something is not.
   *
   * Drains are **serialized rather than deduplicated**: each call queues behind
   * the one before it and the caller awaits its own turn. Two senders walking
   * the queue at once would send one intent twice — the server would deduplicate
   * it, that is what the key is for, but the device would have shown the owner
   * two answers to one action. And a caller that awaited a drain which had
   * already started before its own enqueue would be told "sent" about work that
   * had not been looked at, which is the failure that makes a test flaky and an
   * iPad wrong.
   */
  async drain(): Promise<void> {
    const next = this.chain.then(
      () => this.runDrain(),
      () => this.runDrain(),
    );
    this.chain = next.catch(() => undefined);
    return next;
  }

  private async runDrain(): Promise<void> {
    for (;;) {
      const next = (await this.all()).find((e) => e.status === 'queued' || e.status === 'inflight');
      if (next === undefined) return;
      if (next.nextAttemptAt > this.now()) return; // Waiting out a backoff. Order holds.

      const keepGoing = await this.attempt(next);
      if (!keepGoing) return;
    }
  }

  /** @returns whether the drain may continue to the next entry. */
  private async attempt(entry: OutboxEntry): Promise<boolean> {
    const request = describeIntent(entry);
    await this.patch(entry.id, { status: 'inflight', attempts: entry.attempts + 1 });

    try {
      const body = await this.api.request({
        method: request.method,
        path: request.path,
        body: request.body,
        idempotencyKey: entry.idempotencyKey,
        schema: request.schema,
      });

      const settled: OutboxEntry = {
        ...entry,
        status: 'done',
        attempts: entry.attempts + 1,
        settledAt: new Date(this.now()).toISOString(),
      };
      // The mirror write and the entry's own settlement, in that order: a crash
      // between them replays an intent whose response is memoized, which returns
      // the same row and converges. The other order could leave a booking that
      // landed with nothing on the device to show for it.
      await this.onSettled({ entry: settled, body });
      await this.db.put('outbox', settled);
      this.onChange();
      return true;
    } catch (e) {
      const failure = e instanceof ApiFailure ? e : new ApiFailure('unavailable', String(e));

      if (isRetryable(failure)) {
        const attempts = entry.attempts + 1;
        await this.patch(entry.id, {
          status: 'queued',
          attempts,
          nextAttemptAt: this.now() + backoffMs(attempts),
          lastError: failure.message,
        });
        // Stop. The next intent may depend on this one having landed.
        return false;
      }

      await this.patch(entry.id, {
        status: 'rejected',
        attempts: entry.attempts + 1,
        settledAt: new Date(this.now()).toISOString(),
        rejection: rejectionFrom(failure, this.now()),
      });
      // A ruling removes this intent from the queue; the rest still owe the
      // cloud their writes.
      return true;
    }
  }

  private async patch(id: string, changes: Partial<OutboxEntry>): Promise<void> {
    const current = await this.db.get('outbox', id);
    if (current === undefined) return;
    await this.db.put('outbox', { ...current, ...changes });
    this.onChange();
  }

  private async nextSeq(): Promise<number> {
    const current = (await readMeta<number>(this.db, META.seq)) ?? 0;
    const next = current + 1;
    await writeMeta(this.db, META.seq, next);
    return next;
  }
}

/**
 * Keep the server's account of the refusal, whole.
 *
 * `conflicts[]` is parsed rather than trusted — it is what the owner's task
 * sentence is built out of, and a shape that drifted would produce copy that
 * says nothing. A detail that does not parse is dropped, not guessed at: the
 * task then reads as a plain "this did not go through", which is less useful and
 * still true.
 */
function rejectionFrom(failure: ApiFailure, nowMs: number): RejectionRecord {
  const rawConflicts = failure.detail?.['conflicts'];
  const conflicts = ConflictingAppointment.array().safeParse(rawConflicts);
  return {
    code: failure.code,
    message: failure.message,
    status: failure.status,
    detail: failure.detail,
    ...(conflicts.success ? { conflicts: conflicts.data } : {}),
    at: new Date(nowMs).toISOString(),
  };
}
